import * as React from 'react';
import { ForecastEvolutionChart, createEvolutionLineConfigs } from '@/components/charts';
import { useForecastTrends } from '@/hooks/api';
import type { StormImpactModel } from '@/types';
import { format } from 'date-fns';
import {
  VariableSelectionDropdown,
  type VariableOption,
} from '@/components/maps/insets/VariableSelectionDropdown';

// Callback-ref approach: observer is set up whenever the element mounts/unmounts.
function useContainerHeight(): [React.RefCallback<HTMLDivElement>, number] {
  const [height, setHeight] = React.useState(0);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  const ref = React.useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        setHeight(entries[0].contentRect.height);
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  return [ref, height];
}

const DEFAULT_EVOLUTION_VARIABLES = ['estimate90th', 'predicted_median', 'estimate75th', 'best_estimate'];

interface EvolutionTileProps {
  nodeId?: string;
  modelId?: string;
  weatherModelId?: string;
  startDate?: Date;
  /** From available-runs — source of truth for which runs to chart. */
  availableRuns?: string[];
  availableRunsLoading?: boolean;
  /**
   * When false, skip network so primary SIP panels (map/timeline/grid) can
   * settle after model/run changes. Defaults to true.
   */
  enabled?: boolean;
  /** Leaf nodes under selected geo scope — scales Evolution fan-out. */
  scopeLeafCount?: number;
  stormImpactModel?: StormImpactModel;
  units?: string;
}

export function EvolutionTile({
  nodeId,
  modelId,
  weatherModelId,
  startDate,
  availableRuns,
  availableRunsLoading = false,
  enabled = true,
  scopeLeafCount = 0,
  stormImpactModel,
  units = 'Customers',
}: EvolutionTileProps) {
  const [chartContainerRef, chartHeight] = useContainerHeight();
  const [selectedEvolutionVariables, setSelectedEvolutionVariables] =
    React.useState<string[]>(DEFAULT_EVOLUTION_VARIABLES);

  const forecastDate = React.useMemo(
    () => (startDate ? format(startDate, 'yyyy-MM-dd') : undefined),
    [startDate]
  );

  const paramsPresent = !!nodeId && !!modelId && !!weatherModelId && !!forecastDate;

  const {
    data: evolutionData,
    loading: evolutionLoading,
    error: evolutionError,
    availableVariables: evolutionAvailableVariables,
    partial: evolutionPartial,
    failedRunCount: evolutionFailedRunCount,
    missingNewestRun: evolutionMissingNewestRun,
  } = useForecastTrends({
    nodeId,
    modelId,
    weatherModelId,
    forecastDate,
    availableRuns,
    availableRunsLoading,
    stormImpactModel,
    enabled,
    scopeLeafCount,
  });

  // Deferral / primary-panel settle: show spinner instead of a false empty state.
  const waitingForPrimary = paramsPresent && !enabled;
  const showLoading =
    waitingForPrimary || availableRunsLoading || evolutionLoading;

  const availableEvolutionVariables = React.useMemo<VariableOption[]>(() => {
    return evolutionAvailableVariables.map((slug) => {
      const variable = stormImpactModel?.variables?.find((v) => v.variable_slug === slug);
      return {
        id: slug,
        slug,
        name: variable?.variable_name || slug.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        units: variable?.variable_units ?? null,
        order: variable?.variable_order ?? 999,
        variableType: variable?.variable_type,
      };
    });
  }, [evolutionAvailableVariables, stormImpactModel?.variables]);

  React.useEffect(() => {
    if (availableEvolutionVariables.length === 0) return;
    const validSelected = selectedEvolutionVariables.filter((v) => evolutionAvailableVariables.includes(v));
    if (validSelected.length === 0) {
      const defaults = DEFAULT_EVOLUTION_VARIABLES.filter((v) => evolutionAvailableVariables.includes(v));
      setSelectedEvolutionVariables(defaults.length > 0 ? defaults : [evolutionAvailableVariables[0]]);
    } else if (validSelected.length !== selectedEvolutionVariables.length) {
      setSelectedEvolutionVariables(validSelected);
    }
  }, [evolutionAvailableVariables, availableEvolutionVariables.length, selectedEvolutionVariables]);

  const handleEvolutionVariablesChange = React.useCallback(
    (newSelection: string[]) => {
      if (newSelection.length === 0) { setSelectedEvolutionVariables([]); return; }
      const prev = selectedEvolutionVariables;
      const added = newSelection.filter((v) => !prev.includes(v));
      if (added.length === 0) { setSelectedEvolutionVariables(newSelection); return; }
      const getUnits = (slug: string) => availableEvolutionVariables.find((av) => av.id === slug || av.slug === slug)?.units ?? null;
      const newVarUnits = getUnits(added[0]);
      const currentUnits = prev.length > 0 ? getUnits(prev[0]) : null;
      if (newVarUnits !== currentUnits) {
        setSelectedEvolutionVariables(newSelection.filter((s) => getUnits(s) === newVarUnits));
      } else {
        setSelectedEvolutionVariables(newSelection);
      }
    },
    [selectedEvolutionVariables, availableEvolutionVariables]
  );

  const formatVariableName = React.useCallback(
    (slug: string) => slug.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
    []
  );

  const evolutionVariableKeys = React.useMemo(
    () => selectedEvolutionVariables.filter((key) => evolutionAvailableVariables.includes(key)),
    [selectedEvolutionVariables, evolutionAvailableVariables]
  );

  const evolutionLines = React.useMemo(() => {
    if (evolutionVariableKeys.length === 0) return [];
    const labels = evolutionVariableKeys.map((key) => {
      const variable = stormImpactModel?.variables?.find((item) => item.variable_slug === key);
      return variable?.variable_name || formatVariableName(key);
    });
    return createEvolutionLineConfigs(evolutionVariableKeys, labels);
  }, [evolutionVariableKeys, stormImpactModel?.variables, formatVariableName]);

  const evolutionYAxisUnits = React.useMemo(() => {
    if (evolutionVariableKeys.length === 0) return units;
    const firstVar = stormImpactModel?.variables?.find((v) => evolutionVariableKeys.includes(v.variable_slug));
    return firstVar?.variable_units || units;
  }, [evolutionVariableKeys, stormImpactModel?.variables, units]);

  if (showLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex items-center justify-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
          <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-200" />
          <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse animation-delay-400" />
        </div>
        <p className="mt-4 text-sm text-muted-foreground">Loading forecast evolution...</p>
      </div>
    );
  }

  if (evolutionError) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <span className="text-xl">⚠️</span>
        </div>
        <h3 className="text-sm font-medium mb-2">Forecast Evolution Unavailable</h3>
        <p className="text-xs text-muted-foreground">Could not load forecast runs for this date. Please retry.</p>
      </div>
    );
  }

  if (evolutionData.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <span className="text-xl">📈</span>
        </div>
        <h3 className="text-sm font-medium mb-2">No Trend Data Available</h3>
        <p className="text-xs text-muted-foreground">No forecast trend data found for different model runs.</p>
      </div>
    );
  }

  if (evolutionVariableKeys.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <span className="text-xl">📊</span>
        </div>
        <h3 className="text-sm font-medium mb-2">No Variables Selected</h3>
        <p className="text-xs text-muted-foreground">Please select at least one variable to display in the chart.</p>
        <div className="mt-4 flex justify-center">
          <VariableSelectionDropdown
            availableVariables={availableEvolutionVariables}
            selectedVariables={selectedEvolutionVariables}
            onSelectedVariablesChange={handleEvolutionVariablesChange}
            formatVariableName={formatVariableName}
            label="Variables"
            defaultVariables={DEFAULT_EVOLUTION_VARIABLES}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-end gap-2 flex-shrink-0 px-3 pt-2">
        {evolutionPartial && (
          <span
            className="mr-auto text-xs text-amber-600 dark:text-amber-500"
            title={`${evolutionFailedRunCount} run(s) failed to load`}
          >
            {evolutionMissingNewestRun
              ? 'Latest run unavailable — showing older runs'
              : `${evolutionFailedRunCount} run(s) unavailable`}
          </span>
        )}
        {availableEvolutionVariables.length > 0 && (
          <VariableSelectionDropdown
            availableVariables={availableEvolutionVariables}
            selectedVariables={selectedEvolutionVariables}
            onSelectedVariablesChange={handleEvolutionVariablesChange}
            formatVariableName={formatVariableName}
            label="Variables"
            defaultVariables={DEFAULT_EVOLUTION_VARIABLES}
          />
        )}
      </div>
      <div ref={chartContainerRef} className="flex-1 min-h-0 px-2 pb-2">
        {chartHeight > 0 && (
          <ForecastEvolutionChart
            data={evolutionData}
            lines={evolutionLines}
            height={chartHeight - 8}
            yAxisLabel={evolutionYAxisUnits}
            showGrid={true}
            showTooltip={true}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
}
